import { NavLink, useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { navLinkClass } from "@/lib/utils";
import { useAuth } from "@/state/AuthContext";
import { trpc } from "@/trpc";

const ADMIN_LINKS = [
  { label: "Contacts", to: "/admin/contacts" },
  { label: "Events", to: "/admin/events" },
  { label: "Meetings", to: "/admin/meetings" },
  { label: "Website", to: "/admin/website" },
  { label: "Media", to: "/admin/media" },
  { label: "Garage", to: "/admin/garage/suggestions" },
] as const;

function PendingBadge({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-yellow-400 px-1.5 py-0.5 text-xs font-semibold text-yellow-900 leading-none">
      {count > 99 ? "99+" : count}
    </span>
  );
}

function useMemberNavData() {
  const { user, isMember, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  const { data: pendingUsersData } = trpc.admin.users.pendingCount.useQuery(undefined, {
    enabled: isAdmin,
  });
  const { data: pendingEventsData } = trpc.admin.rsvps.listPendingByAllEvents.useQuery(undefined, {
    enabled: isAdmin,
  });
  const { data: pendingGarageData } = trpc.admin.bikeSuggestions.pendingCount.useQuery(undefined, {
    enabled: isAdmin,
  });
  const { data: pendingMediaData } = trpc.admin.media.pendingCount.useQuery(undefined, {
    enabled: isAdmin,
  });

  const totalPendingCount =
    (pendingUsersData?.registration_count ?? 0) +
    (pendingEventsData ?? []).reduce((sum, ev) => sum + ev.pendingCount, 0) +
    (pendingGarageData?.count ?? 0) +
    (pendingMediaData?.count ?? 0);

  async function handleLogout(close: () => void) {
    close();
    await logout();
    navigate("/login");
  }

  // The bare /profile route redirects to your own profile once auth resolves.
  const profilePath = user ? `/profile/${user.username}` : "/profile";

  return { isMember, isAdmin, totalPendingCount, handleLogout, profilePath };
}

export function MemberNavLinks({ onClose }: { onClose?: () => void }) {
  const close = onClose ?? (() => {});
  const { isMember, isAdmin, totalPendingCount, handleLogout, profilePath } = useMemberNavData();

  return (
    <>
      <NavLink to="/" end onClick={close} className={navLinkClass}>
        Dashboard
      </NavLink>
      <NavLink to="/events" onClick={close} className={navLinkClass}>
        Events
      </NavLink>
      {/* Not member-gated: gallery viewing is deliberately open to any signed-in user. */}
      <NavLink to="/gallery" onClick={close} className={navLinkClass}>
        Gallery
      </NavLink>
      {isMember && (
        <NavLink to="/roster" onClick={close} className={navLinkClass}>
          Roster
        </NavLink>
      )}
      {isAdmin && (
        <>
          {/* Desktop: pure-CSS hover dropdown (no JS open state — the whole
              trigger + menu subtree is one `group`, shown via group-hover /
              group-focus-within so the browser's own hit-testing drives it;
              nothing can race a portal or a debounce timer). */}
          <div className="group relative hidden md:flex items-center md:ml-2 md:pl-2 md:border-l md:border-white/20">
            <NavLink to="/admin" className={navLinkClass}>
              Admin
              <PendingBadge count={totalPendingCount} />
            </NavLink>
            <button
              className="flex items-center px-1 py-2 text-white/70 hover:text-white focus:outline-none"
              aria-label="Admin submenu"
              aria-haspopup="menu"
            >
              <ChevronDown className="size-3.5" />
            </button>
            <div
              role="menu"
              className="absolute right-0 top-full z-50 w-44 rounded-md border bg-popover p-1 text-popover-foreground shadow-md opacity-0 invisible transition-opacity group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible"
            >
              {ADMIN_LINKS.map(({ label, to }) => (
                <NavLink
                  key={to}
                  to={to}
                  role="menuitem"
                  className="block cursor-pointer rounded-sm px-2 py-1.5 text-sm outline-hidden hover:bg-satyrs-blue/20 hover:text-white focus:bg-satyrs-blue/20 focus:text-white"
                >
                  {label}
                </NavLink>
              ))}
            </div>
          </div>

          {/* Mobile: flat indented links */}
          <div className="md:hidden flex flex-col gap-0.5">
            <NavLink to="/admin" end onClick={close} className={navLinkClass}>
              Admin
              <PendingBadge count={totalPendingCount} />
            </NavLink>
            <div className="pl-4 flex flex-col gap-0.5">
              {ADMIN_LINKS.map(({ label, to }) => (
                <NavLink key={to} to={to} onClick={close} className={navLinkClass}>
                  {label}
                </NavLink>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Settings + logout — mobile only, always rendered */}
      <div className="md:hidden mt-2 pt-2 border-t border-white/10 flex flex-col gap-0.5">
        <NavLink to={profilePath} onClick={close} className={navLinkClass}>
          Profile
        </NavLink>
        <NavLink to="/settings/profile" onClick={close} className={navLinkClass}>
          Settings
        </NavLink>
        <button onClick={() => handleLogout(close)} className={navLinkClass({ isActive: false })}>
          Log Out
        </button>
      </div>
    </>
  );
}
